import { Reveal } from "@/components/Reveal";
import { SectionLabel, CTAGroup } from "@/components/bits";
import LeadDialog from "@/components/LeadDialog";
import { PLANS } from "@/lib/site";
import { Check } from "lucide-react";

export default function CarePlans() {
  return (
    <div data-testid="page-care-support" className="pt-32">
      <header className="mx-auto max-w-[1600px] px-6 pb-16 md:px-12">
        <Reveal>
          <SectionLabel>Care &amp; Support</SectionLabel>
          <h1 className="mt-8 max-w-5xl font-serif text-5xl font-300 leading-[0.98] md:text-8xl">
            Concierge <span className="italic accent-text">membership</span> support.
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60 md:text-lg">
            Tailored care that keeps every system performing — from proactive monitoring and off-site reboots to on-site health checks, event readiness, and priority service. Annual pricing available upon request.
          </p>
        </Reveal>
      </header>

      <section className="mx-auto max-w-[1600px] px-6 pb-24 md:px-12" data-testid="service-plans">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {PLANS.map((plan, i) => (
            <Reveal
              key={plan.name}
              delay={i * 0.08}
              className={`relative flex flex-col border p-10 ${
                plan.featured
                  ? "border-transparent [background:linear-gradient(#0a0a0c,#0a0a0c)_padding-box,linear-gradient(135deg,#3b82f6,#10b981)_border-box]"
                  : "border-white/10 bg-isav-bg"
              }`}
              data-testid={`plan-${plan.name.toLowerCase()}`}
            >
              {plan.featured && (
                <span className="absolute right-6 top-6 border border-isav-green/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-isav-green">
                  Most popular
                </span>
              )}
              <h3 className="font-serif text-3xl font-400">{plan.name}</h3>
              <p className="mt-2 text-sm italic text-white/50">{plan.tagline}</p>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
                Available upon request
              </p>
              <div className="my-8 h-px w-full bg-white/10" />
              <ul className="flex-1 space-y-3">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/70">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-isav-green" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <LeadDialog
                  kind="consultation"
                  variant={plan.featured ? "accent" : "secondary"}
                  triggerLabel={`Enquire — ${plan.name}`}
                  triggerClassName="w-full !px-6"
                  testId={`plan-cta-${plan.name.toLowerCase()}`}
                />
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mt-10 max-w-2xl text-[11px] leading-relaxed text-white/35">
          Plan inclusions shown are representative; final terms, inclusions, and annual pricing are confirmed upon request and subject to terms &amp; conditions.
        </p>
      </section>

      <section className="border-t border-white/10 bg-isav-bg2 py-24" data-testid="care-cta">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <Reveal>
            <h2 className="max-w-3xl font-serif text-4xl font-300 md:text-6xl">
              Not sure which plan fits your home?
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/55 md:text-base">
              Book a consultation and we'll recommend the right level of ongoing care for your systems.
            </p>
            <div className="mt-10"><CTAGroup /></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
